<template>
    <SearchResultsPanel
        :title="title"
        @viewAll="viewAll"
        :showCount="showCount"
        :showViewAll="showViewAll"
        :count="interviews.meta.resultCount"
    >
        <ul>
            <li v-for="interview in displayInterviews" :key="interview.id">
                <router-link
                    :to="interview.to"
                    @click.native.capture="event => $emit('navigating', event, interview)"
                    class="search-result"
                >
                    <ExecutiveBadge
                        :executive="interview.executive"
                        show-position
                        size="medium"
                        :query="{ tab: 'Premium Research', interviewId: interview.id }"
                        hide-popover
                    />

                    <span class="interview-title">
                        {{ interview.title }}
                    </span>
                    <div class="snippet" v-if="interview.highlights.length > 0">
                        <div
                            v-for="(highlight, index) in interview.highlights"
                            :key="`highlight-${interview.id}-${index}`"
                        >
                            <span v-html="highlight.content"></span>
                        </div>
                    </div>
                </router-link>
            </li>
        </ul>
    </SearchResultsPanel>
</template>
<script setup>
import { computed } from 'vue'
import ExecutiveBadge from '../executives/executive-badge.vue'
import SearchResultsPanel from './SearchResultsPanel.vue'

const props = defineProps({
    interviews: {
        type: Object,
        required: true,
    },
    maxItems: {
        type: Number,
        required: true,
    },
    query: {
        type: String,
        required: false,
    },
    showCount: {
        type: Boolean,
        default: true,
    },
    showViewAll: {
        type: Boolean,
        required: true,
    },
    title: {
        type: String,
        default: 'Interview Results',
    },
})

const emit = defineEmits(['viewAll'])

const highlighted = computed(() => {
    return props.interviews.results.map(result => {
        const interview = result.entity

        let tab = 'Overview'

        const highlights = result.highlights
            .filter(h => !h.field.startsWith('@') && !h.field.startsWith('keyQuote'))
            .flatMap(h =>
                h.content.map(c => ({
                    field: h.field,
                    content: c,
                }))
            )

        const highlight = highlights[0]

        if (highlight && highlight.field.startsWith('questions')) {
            tab = 'Transcript'
        }

        return {
            ...interview,
            highlights,
            to: {
                name: 'executive-page',
                params: { id: interview.executive.id },
                query: {
                    tab: 'Premium Research',
                    interviewId: interview.id,
                    'interview.highlight': props.query?.trim(),
                    'interview.tab': tab,
                },
            },
        }
    })
})

const displayInterviews = computed(() => highlighted.value.slice(0, props.maxItems))
function viewAll() {
    emit('viewAll')
}
</script>
<style scoped>
ul {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.search-result {
    display: grid;
    flex-wrap: wrap;
    padding: 16px 0;
    gap: 8px 24px;
    grid-template-columns: 3fr 1fr;
    grid-template-areas:
        'badge detail'
        'snippet snippet';
    width: 100%;
}

.snippet {
    border-top: 1px dotted black;
    font-style: italic;
    grid-area: snippet;
    padding-top: 8px;
    margin-left: 72px;
    cursor: default;

    > * {
        display: block;
        line-height: 2;
    }

    :deep(.search-result-highlight) {
        color: black;
        font-weight: bold;
    }
}

@media (max-width: 768px) {
    .search-result {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .snippet {
            margin: 0 auto;
            width: 95%;
        }
    }
}
</style>
